import React, {useCallback, useMemo} from "react";
import {Checkbox} from "antd";
import {buildColoursConfig, manageColours, getColourOnColour} from "./ManageColours";
import {
    ContentStyleEditGroup,
    ContentStylesColourBlock, ContentStylesDemoSubsection, ContentStylesDemoSubsectionVariants, resolveVariant
} from "../ContentStylesHelper";

const EDIT_TYPES = {
    MAIN: "main",
    ON_MAIN: "on-main",
    HEADING_A: "cont-head-a",
    HEADING_B: "cont-head-b",
    HEADING_C: "cont-head-c",
    HEADING_D: "cont-head-d",
    EMPH: "cont-emph",
    EMPH_COLOUR: "cont-emph-colour",
    TEXT_COLOUR: "cont-colour-main",
    TEXT_COLOUR_CUSTOM: "cont-colour-custom-*",
    BG_TEXT_COLOUR_CUSTOM: "cont-colour-bg-custom-*",
    HEADINGS: "headings",
}

export const textStylesRegistry = {
    [EDIT_TYPES.MAIN]: {title: "Main colour", cssVar: ["--cont-main-colour", "--cont-main-colour-RGB"], mainColour: true},
    [EDIT_TYPES.ON_MAIN]: {title: "On main text colour", cssVar: ["--cont-on-main-colour"], autoGenerated: true},
    [EDIT_TYPES.HEADINGS]: {title: "Headings"},
    [EDIT_TYPES.HEADING_A]: {title: "Heading A", cssVar: ["--cont-heading-colour-A"]},
    [EDIT_TYPES.HEADING_B]: {title: "Heading B", cssVar: ["--cont-heading-colour-B"]},
    [EDIT_TYPES.HEADING_C]: {title: "Heading C", cssVar: ["--cont-heading-colour-C"]},
    [EDIT_TYPES.HEADING_D]: {title: "Heading D", cssVar: ["--cont-heading-colour-D"]},
    [EDIT_TYPES.EMPH]: {title: "Emphasis text", slave: EDIT_TYPES.MAIN},
    [EDIT_TYPES.EMPH_COLOUR]: {title: "Emphasis text", cssVar: ["--cont-emph-colour"]},
    [EDIT_TYPES.TEXT_COLOUR]: {title: "Text colour", slave: EDIT_TYPES.MAIN},
    [EDIT_TYPES.TEXT_COLOUR_CUSTOM]: {title: "Custom text colour @", cssVar: ["--cont-custom-colour-*"], totalVariants: 10},
    [EDIT_TYPES.BG_TEXT_COLOUR_CUSTOM]: {title: "Custom background colour @", totalVariants: 10, slave: EDIT_TYPES.TEXT_COLOUR_CUSTOM},
}

export const TextStyles = props => {
    const {editCategory, updateStylesCallback} = props;

    const coloursConfig = useMemo(() => buildColoursConfig(textStylesRegistry), []);
    const generateColours = useCallback((mainColour) => ({
        [EDIT_TYPES.ON_MAIN]: getColourOnColour(mainColour)
    }), []);
    const {colours, updateColours, toggleGeneratedColours} = manageColours(
        editCategory,
        coloursConfig,
        generateColours,
        updateStylesCallback
    );

    const onMainAuto = colours[EDIT_TYPES.ON_MAIN]?.autoGenerated;

    return (
        <>
            <h5>Colours</h5>
            <ContentStylesColourBlock
                editType={EDIT_TYPES.MAIN}
                colourConfig={textStylesRegistry[EDIT_TYPES.MAIN]}
                colours={colours}
                onChange={updateColours}
            />
            <Checkbox
                checked={!!onMainAuto}
                onChange={(event) => toggleGeneratedColours(event.target.checked)}
            >
                Auto generated colours
            </Checkbox>
            <span className="ch-text-small margin-t--s">* Color can be auto-generated based on main color.</span>
            <ContentStylesColourBlock
                editType={EDIT_TYPES.ON_MAIN}
                colourConfig={textStylesRegistry[EDIT_TYPES.ON_MAIN]}
                colours={colours}
                onChange={updateColours}
            />
            <ContentStyleEditGroup
                editType={EDIT_TYPES.HEADINGS}
            >
                <ContentStylesColourBlock
                    editType={EDIT_TYPES.HEADING_A}
                    colourConfig={textStylesRegistry[EDIT_TYPES.HEADING_A]}
                    colours={colours}
                    onChange={updateColours}
                />
                <ContentStylesColourBlock
                    editType={EDIT_TYPES.HEADING_B}
                    colourConfig={textStylesRegistry[EDIT_TYPES.HEADING_B]}
                    colours={colours}
                    onChange={updateColours}
                />
                <ContentStylesColourBlock
                    editType={EDIT_TYPES.HEADING_C}
                    colourConfig={textStylesRegistry[EDIT_TYPES.HEADING_C]}
                    colours={colours}
                    onChange={updateColours}
                />
                <ContentStylesColourBlock
                    editType={EDIT_TYPES.HEADING_D}
                    colourConfig={textStylesRegistry[EDIT_TYPES.HEADING_D]}
                    colours={colours}
                    onChange={updateColours}
                />
            </ContentStyleEditGroup>
            <ContentStylesColourBlock
                editType={EDIT_TYPES.EMPH_COLOUR}
                colourConfig={textStylesRegistry[EDIT_TYPES.EMPH_COLOUR]}
                colours={colours}
                onChange={updateColours}
            />
            {[...Array(textStylesRegistry[EDIT_TYPES.TEXT_COLOUR_CUSTOM].totalVariants)].map((_, i) => {
                const variantNum = i + 1;
                const resolvedEditType = resolveVariant(EDIT_TYPES.TEXT_COLOUR_CUSTOM, variantNum);

                return (
                    <ContentStylesColourBlock
                        key={`custom-${variantNum}`}
                        editType={resolvedEditType}
                        variant={variantNum}
                        colourConfig={textStylesRegistry[EDIT_TYPES.TEXT_COLOUR_CUSTOM]}
                        colours={colours}
                        onChange={updateColours}
                    />
                );
            })}
        </>
    )
}

export const TextStylesDemo = props => {
    const {demoEvents} = props;

    return (
        <>
            <ContentStylesDemoSubsection title="Headings" groupKey={EDIT_TYPES.HEADINGS}>
                <span className={EDIT_TYPES.HEADING_A} {...demoEvents(EDIT_TYPES.HEADING_A)}>
                    Heading A
                </span>
                <span className={EDIT_TYPES.HEADING_B} {...demoEvents(EDIT_TYPES.HEADING_B)}>
                    Heading B
                </span>
                <span className={EDIT_TYPES.HEADING_C} {...demoEvents(EDIT_TYPES.HEADING_C)}>
                    Heading C
                </span>
                <span className={EDIT_TYPES.HEADING_D} {...demoEvents(EDIT_TYPES.HEADING_D)}>
                    Heading D
                </span>
            </ContentStylesDemoSubsection>
            <ContentStylesDemoSubsection title="On Main text">
                <p
                    className={EDIT_TYPES.ON_MAIN}
                    {...demoEvents(EDIT_TYPES.ON_MAIN)}
                    style={{background: "var(--cont-main-colour)", color: "var(--cont-on-main-colour)"}}
                >
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
            </ContentStylesDemoSubsection>
            <ContentStylesDemoSubsection title="Emphasis text">
                <p className={EDIT_TYPES.EMPH} {...demoEvents(EDIT_TYPES.EMPH)}>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
            </ContentStylesDemoSubsection>
            <ContentStylesDemoSubsection title="Emphasis text custom colour">
                <p className={EDIT_TYPES.EMPH_COLOUR} {...demoEvents(EDIT_TYPES.EMPH_COLOUR)}>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
            </ContentStylesDemoSubsection>
            <ContentStylesDemoSubsection title="Coloured text">
                <p>
                    <span className={EDIT_TYPES.TEXT_COLOUR} {...demoEvents(EDIT_TYPES.TEXT_COLOUR)}>
                        Coloured text, main colour
                    </span>
                </p>
                <ContentStylesDemoSubsectionVariants
                    stylesRegistryData={textStylesRegistry[EDIT_TYPES.TEXT_COLOUR_CUSTOM]}
                    baseClassKey={EDIT_TYPES.TEXT_COLOUR_CUSTOM}
                    render={(variantNum, classKey) => (
                        <p>
                            <span className={classKey} {...demoEvents(classKey)}>
                                Coloured text, custom colour {variantNum}
                            </span>
                        </p>
                    )}
                />
            </ContentStylesDemoSubsection>
            <ContentStylesDemoSubsection title="Coloured background text">
                <ContentStylesDemoSubsectionVariants
                    stylesRegistryData={textStylesRegistry[EDIT_TYPES.BG_TEXT_COLOUR_CUSTOM]}
                    baseClassKey={EDIT_TYPES.BG_TEXT_COLOUR_CUSTOM}
                    render={(variantNum, classKey) => (
                        <p>
                            <span className={classKey} {...demoEvents(classKey)}>
                                Coloured background text, custom colour {variantNum}
                            </span>
                        </p>
                    )}
                />
            </ContentStylesDemoSubsection>
        </>
    )
}